import React, { useState, useEffect, useMemo } from 'react';
import { Box, Autocomplete, TextField } from '@mui/material';
import {ReactComponent as Segmentation_people} from '../Layer_images/Settings_images/segmentation_people.svg'
import {ReactComponent as Segmentation_calendar} from '../Layer_images/Settings_images/segmentation_calendar.svg'
import {ReactComponent as Segmentation_dollar} from '../Layer_images/Settings_images/segmentation_dollar.svg'

const age_mappings = {
    'under_18': 'Youth',
    '18_24': 'Young Adults',
    '25_34': 'Establishing Adults',
    '35_44': 'Mid-Career Adults',
    '45_54': 'Established Adults',
    '55_64': 'Pre-Retiree',
    '65_plus': 'Retired'
}

const age_segments = {
    'under_18': 'Under 18',
    '18_24': '18 - 24',
    '25_34': '25 - 34',
    '35_44': '35 - 44',
    '45_54': '45 - 54',
    '55_64': '55 - 64',
    '65_plus': '65+'
}

const income_segments = {
    'low_income': '$0-30,000',
    'lower_middle_income': '$30,000-50,000',
    'middle_income': '$50,000-75,000',
    'upper_middle_income': '$75,000-125,000',
    'high_income': '$125,000-200,000',
    'very_high_income': '$200,000+'
}

const income_mapping = {
    low_income: 'Essential Income',
    lower_middle_income: 'Building Income',
    middle_income: 'Stable Income',
    upper_middle_income: 'Comfortable Income',
    high_income: 'Affluent Income',
    very_high_income: 'Elite Income',
}

const region_mapping = {
    Urban_Core: 'Metro',
    Urban: 'City',
    Suburban: 'Suburbs',
    Rural: 'Rural'
}

const age_options = [
    {label: 'All Ages', value: 'all'},
    {label: 'Under 18', value: 'under_18'},
    {label: '18-24', value: '18_24'},
    {label: '25-34', value: '25_34'},
    {label: '35-44', value: '35_44'},
    {label: '45-54', value: '45_54'},
    {label: '55-64', value: '55_64'},
    {label: '65+', value: '65_plus'},
];

const income_options = [
    {label: 'All Income Levels', value: 'all'},
    {label: '$0-30k', value: 'low_income'},
    {label: '$30k-50k', value: 'lower_middle_income'},
    {label: '$50k-75k', value: 'middle_income'},
    {label: '$75k-125k', value: 'upper_middle_income'},
    {label: '$125k-200k', value: 'high_income'},
    {label: '$200k+', value: 'very_high_income'},
];

const region_options = [
    {label: 'Metro', value: 'Urban Core'},
    {label: 'City', value: 'Urban'},
    {label: 'Suburbs', value: 'Suburban'},
    {label: 'Rural', value: 'Rural'},
];

// Cache for processed segmentation data
const segmentationCache = new Map();

// Autocomplete field styles
const autocompleteFieldStyles = {
    '& .MuiOutlinedInput-root': {
        fontSize: '14px',
        '& fieldset': {
            borderColor: '#E0E0E0',
        },
        '&:hover fieldset': {
            borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#1976d2',
        },
    },
    '& .MuiInputLabel-root': {
        fontSize: '14px',
        color: '#666',
    },
};

export const OverviewMarketSegmentation = ({analyticsData}) => {
    const [marketSegmentations, setMarketSegmentations] = useState([]);
    const [selectedAge, setSelectedAge] = useState({label: 'All Ages', value: 'all'});
    const [selectedIncome, setSelectedIncome] = useState({label: 'All Income Levels', value: 'all'});

    // Create a cache key based on the analyticsData.current and filters
    const cacheKey = useMemo(() => {
        if (!analyticsData?.current) return null;
        return JSON.stringify({
            data: analyticsData.current,
            ageFilter: selectedAge.value,
            incomeFilter: selectedIncome.value
        });
    }, [analyticsData?.current, selectedAge.value, selectedIncome.value]);

    // Process analytics data to extract age-income combinations
    const processAnalyticsData = useMemo(() => {
        if (!analyticsData?.current || !cacheKey) return [];

        // Check cache first
        if (segmentationCache.has(cacheKey)) {
            return segmentationCache.get(cacheKey);
        }

        const current = analyticsData.current;
        const final_values = [];

        // Extract all keys that match the age_income pattern
        Object.entries(current).forEach(([key, value]) => {
            // Look for keys that start with age groups and contain income levels
            if (key.includes('_') && typeof value === 'number') {
                // Check if key matches age_income pattern (e.g., "18_24 low_income")
                const parts = key.split(' ');
                if (parts.length === 2) {
                    const [agePart, incomePart] = parts;
                    
                    // Verify it's a valid age group
                    if (age_mappings[agePart] && income_mapping[incomePart]) {
                        final_values.push({
                            total_pop: value,
                            age_group: agePart,
                            income_group: incomePart,
                            region_type: null // No region data in this format
                        });
                    }
                }
            }
        });

        // Sort by total population and take top 10
        let sortedValues = final_values
            .sort((a, b) => (b?.total_pop || 0) - (a?.total_pop || 0));

        // Apply filters
        if (selectedAge.value !== 'all') {
            sortedValues = sortedValues.filter(item => item.age_group === selectedAge.value);
        }
        
        if (selectedIncome.value !== 'all') {
            sortedValues = sortedValues.filter(item => item.income_group === selectedIncome.value);
        }

        // Take top 10 after filtering
        sortedValues = sortedValues.slice(0, 10);

        // Cache the result
        segmentationCache.set(cacheKey, sortedValues);
        
        return sortedValues;
    }, [analyticsData?.current, cacheKey, selectedAge.value, selectedIncome.value]);

    useEffect(() => {
        setMarketSegmentations(processAnalyticsData);
    }, [processAnalyticsData]);

    // Filter change handlers
    const handleAgeChange = (event, newValue) => {
        setSelectedAge(newValue || {label: 'All Ages', value: 'all'});
    };

    const handleIncomeChange = (event, newValue) => {
        setSelectedIncome(newValue || {label: 'All Income Levels', value: 'all'});
    };

    const get_image_link = (age, income) => {
        var income_value = 1;
        if(age != 'under_18'){
            const income_mapping_img = { 
                'low_income': 1, 
                'lower_middle_income': 2, 
                'middle_income': 3, 
                'upper_middle_income': 4, 
                'high_income': 5,  
                'very_high_income': 6
            }
            income_value = income_mapping_img[income]
        }
        
        return `segmentation/${age}_${income_value}.jpeg`
    }

    function formatNumber(value) {
        // Check if the value is a number
        if (typeof value === 'number' || (typeof value === 'string' && !isNaN(value))) {
          // Convert to number to handle string numbers, then to string for formatting
          return Number(value).toLocaleString();
        } else {
          // Return the original value if it's not a number
          return value;
        }
    }

    return (
        <div>
            <div className="topLineClass" >

            <h2 className='mt-20 mb-20 font-weight600 data-component'>Top Market Segmentations</h2>
                <div className='box-container p-10'>
                    {/* <div className="Competitor-financials-title">Top Market Segmentations</div> */}
                    
                    {/* Filter Section */}
                    <div className="react-class-filter" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start', margin: '20px 0px'}}>
                        <Box sx={{ minWidth: '200px', flex: 1 }}>
                            <Autocomplete
                                options={age_options}
                                getOptionLabel={(option) => option?.label || ""}
                                value={selectedAge}
                                onChange={handleAgeChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Age Group"
                                        placeholder="Select age group"
                                        sx={autocompleteFieldStyles}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ minWidth: '200px', flex: 1 }}>
                            <Autocomplete
                                options={income_options}
                                getOptionLabel={(option) => option?.label || ""}
                                value={selectedIncome}
                                onChange={handleIncomeChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Income Level"
                                        placeholder="Select income level"
                                        sx={autocompleteFieldStyles}
                                    />
                                )}
                            />
                        </Box>
                    </div>
                    {marketSegmentations.map((e) => (
                        <div key={e.age_group + e.income_group + (e.region_type || '')} className='data-component'>
                            <div className='box-container mtb-10 p-10'>
                                <div className='display-flex gap-10 sidbar-primary-text'>
                                    {e.age_group && <div className='display-flex gap-10'><div>{age_mappings[e.age_group]}</div><div>•</div></div>}
                                    {e.income_group && <div className='display-flex gap-10'><div>{income_mapping[e.income_group]}</div>{e.region_type && <div>•</div>}</div>}
                                    {e.region_type && <div className='display-flex gap-10'><div>{region_mapping[e.region_type]}</div></div>}
                                </div>
                                <div className='display-flex gap-10 mtb-10 sidbar-primary-text'>
                                    <img src={get_image_link(e.age_group, e.income_group)} className='segmentation-profile-image'/>
                                    <div className="box-container">
                                        <div className='segmentation-top-sidebar'>
                                            <div className='display-inline-block pt-5'><Segmentation_people/></div>
                                            <div className='display-inline-block font-size-10'>Number of People</div>
                                        </div>
                                        <div className='text-align-left pl-5' style={{color: '#1570EF'}}>{formatNumber(e.total_pop)}</div>
                                    </div>
                                    <div className="box-container">
                                        <div className='segmentation-top-sidebar'>
                                            <div className='display-inline-block pt-5'><Segmentation_calendar/></div>
                                            <div className='display-inline-block font-size-10'>Age Group</div>
                                        </div>
                                        <div className='text-align-left pl-5'>{age_segments[e.age_group]}</div>
                                    </div>
                                    <div className="box-container">
                                        <div className='segmentation-top-sidebar'>
                                            <div className='display-inline-block pt-5'><Segmentation_dollar/></div>
                                            <div className='display-inline-block font-size-10'>Income Group</div>
                                        </div>
                                        <div className='text-align-left pl-5'>{income_segments[e.income_group]}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}